import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { legalDocKeyFromSlug, type CountryCode } from '@capsico/contracts';
import { useI18n } from '../i18n';
import {
  guessCountry,
  legalLocaleFor,
  resolveLegalHref,
  siteLegalUrl,
  useLegalDoc,
} from '../hooks/useLegal';
import { LegalDocBody } from '../components/Legal';
import { Button, EmptyState, ListSkeleton, Notice, PageHeader } from '../components/ui';

/** Account › Legal › one document, read in the app (the same text as capsicoeats.com/legal). */
export function LegalPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const country: CountryCode =
    params.get('country') === 'NP'
      ? 'NP'
      : params.get('country') === 'IN'
        ? 'IN'
        : guessCountry(locale);
  const lang = legalLocaleFor(country, locale);
  const known = legalDocKeyFromSlug(slug) !== null;
  const doc = useLegalDoc(slug, country, lang);
  const siteUrl = siteLegalUrl(slug, country, lang);

  return (
    <div className="min-h-dvh bg-surface">
      <PageHeader
        title={t('legal.title')}
        backLabel={t('common.back')}
        onBack={() => navigate(-1)}
      />
      <div className="px-4 pt-4 pb-10">
        {!known ? (
          <EmptyState icon="file" title={t('legal.loadFailed')} />
        ) : doc.isLoading ? (
          <ListSkeleton rows={6} />
        ) : doc.data ? (
          <>
            <LegalDocBody
              title={doc.data.title}
              meta={t('legal.version', { version: doc.data.version })}
              body={doc.data.body}
              resolveHref={resolveLegalHref}
            />
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 block text-center text-sm font-semibold text-brand-ink"
            >
              {t('legal.openSite')}
            </a>
          </>
        ) : (
          <div className="space-y-4">
            <Notice tone="warning" title={t('legal.loadFailed')}>
              {t('legal.loadFailedBody')}
            </Notice>
            <Button full onClick={() => window.open(siteUrl, '_blank', 'noopener')}>
              {t('legal.openSite')}
            </Button>
            <Button full tone="outline" onClick={() => void doc.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
